import { createAsyncThunk } from "@reduxjs/toolkit";
import {
  deactivateOrganization,
  DeactivateOrganizationBody,
  fetchDeactivationPreview,
  fetchOrganizationAncestors,
  fetchOrganizationsV3,
  fetchOrganizationsV3Stats,
  OrganizationsV3QueryParams,
  renameOrganization,
  RenameOrganizationBody,
} from "../../services/OrganizationsV3Service";
import { fetchOrganizationDetails } from "../../services/OrganizationService";

// Error envelopes are shaped { "msg": "..." }; surface that message to the UI.
const toErrorMessage = (error: unknown): string => {
  const err = error as { response?: { data?: { msg?: string } }; message?: string };
  return err.response?.data?.msg || err.message || "An unexpected error occurred";
};

export const fetchOrganizationsV3Async = createAsyncThunk(
  "organizationsV3/fetchList",
  async (params: OrganizationsV3QueryParams, { rejectWithValue }) => {
    try {
      return await fetchOrganizationsV3(params);
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

export const fetchOrganizationsV3StatsAsync = createAsyncThunk(
  "organizationsV3/fetchStats",
  async (parentId: number | undefined, { rejectWithValue }) => {
    try {
      return await fetchOrganizationsV3Stats(parentId);
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

// The signed-in user's own organization, shown in the top-level page title.
// The account payload only carries organization_id, so resolve the name here.
export const fetchUserOrganizationAsync = createAsyncThunk(
  "organizationsV3/fetchUserOrganization",
  async (orgId: number, { rejectWithValue }) => {
    try {
      const organization = await fetchOrganizationDetails(orgId);
      return { name: organization?.name as string };
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

// Breadcrumb trail for the drilled-into org. The payload carries the orgId so
// the page can ignore a chain that belongs to a previously viewed org.
export const fetchOrganizationAncestorsAsync = createAsyncThunk(
  "organizationsV3/fetchAncestors",
  async (orgId: number, { rejectWithValue }) => {
    try {
      const ancestors = await fetchOrganizationAncestors(orgId);
      return { orgId, ancestors };
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

export const fetchDeactivationPreviewAsync = createAsyncThunk(
  "organizationsV3/fetchDeactivationPreview",
  async (orgId: number, { rejectWithValue }) => {
    try {
      return await fetchDeactivationPreview(orgId);
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

export const deactivateOrganizationAsync = createAsyncThunk(
  "organizationsV3/deactivate",
  async (
    { orgId, body }: { orgId: number; body: DeactivateOrganizationBody },
    { rejectWithValue }
  ) => {
    try {
      return await deactivateOrganization(orgId, body);
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);

export const renameOrganizationAsync = createAsyncThunk(
  "organizationsV3/rename",
  async (
    { orgId, body }: { orgId: number; body: RenameOrganizationBody },
    { rejectWithValue }
  ) => {
    try {
      return await renameOrganization(orgId, body);
    } catch (error: unknown) {
      return rejectWithValue(toErrorMessage(error));
    }
  }
);
